/**
 * Small presentational primitives shared by the wireframe-fidelity screens.
 * Ported from `dev-spec/v1/contenthub-app.jsx` (Badge / PlatformIcon /
 * WidgetCard / Modal). Pure UI — no data fetching.
 */
import type { ReactNode } from 'react';

const badgeStyles: Record<string, string> = {
  draft: 'bg-amber-50 text-amber-700 border-amber-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  review: 'bg-amber-50 text-amber-700 border-amber-200',
  scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
  approved: 'bg-blue-50 text-blue-700 border-blue-200',
  published: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  connected: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  disconnected: 'bg-stone-100 text-stone-500 border-stone-200',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export function Badge({ status, size = 'sm' }: { status: string; size?: 'xs' | 'sm' }) {
  const sz = size === 'xs' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs';
  return (
    <span className={`${sz} rounded-full font-medium border ${badgeStyles[status] || badgeStyles.draft}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

const platformIcons: Record<string, { bg: string; text: string }> = {
  linkedin: { bg: 'bg-blue-600', text: 'in' },
  twitter: { bg: 'bg-stone-900', text: '𝕏' },
  facebook: { bg: 'bg-blue-500', text: 'f' },
  instagram: { bg: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400', text: '◷' },
  tiktok: { bg: 'bg-stone-900', text: '♪' },
  youtube: { bg: 'bg-red-600', text: '▶' },
  pinterest: { bg: 'bg-red-600', text: 'P' },
  threads: { bg: 'bg-stone-900', text: '@' },
  snapchat: { bg: 'bg-yellow-400', text: '◐' },
};

export function PlatformIcon({ platform, size = 'sm' }: { platform: string; size?: 'sm' | 'md' | 'lg' }) {
  const sz = size === 'sm' ? 'w-6 h-6 text-xs' : size === 'md' ? 'w-8 h-8 text-sm' : 'w-10 h-10 text-base';
  const icon = platformIcons[platform] || { bg: 'bg-stone-300', text: '?' };
  return <div className={`${sz} ${icon.bg} text-white rounded flex items-center justify-center font-bold flex-shrink-0`}>{icon.text}</div>;
}

export function WidgetCard({ title, action, actionLabel, children }: {
  title: string;
  action?: () => void;
  actionLabel?: string;
  children: ReactNode;
}) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-stone-100">
        <h3 className="font-semibold text-stone-900">{title}</h3>
        {action && <button onClick={action} className="text-sm text-violet-600 hover:text-violet-700 font-medium">{actionLabel || 'View all'}</button>}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export function Modal({ title, onClose, children, size = 'md' }: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  size?: 'md' | 'lg' | 'xl';
}) {
  const sz = size === 'lg' ? 'max-w-2xl' : size === 'xl' ? 'max-w-4xl' : 'max-w-lg';
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}
        className={`bg-white rounded-xl shadow-2xl w-full ${sz} max-h-[90vh] overflow-hidden flex flex-col`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <h2 className="text-lg font-semibold text-stone-900">{title}</h2>
          <button onClick={onClose} aria-label="Close dialog" className="text-stone-400 hover:text-stone-600 text-xl">&times;</button>
        </div>
        <div className="p-6 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
